import { UNITS_OF_MEASURE } from '../constants/unitsOfMeasure';

const CATEGORY_TO_INT = {
  Flower: 0,
  Edible: 1,
  Vape: 2,
  Topical: 3
};

export function buildLookups({ organizations = [], markets = [], brands = [], landingPages = [], facilities = [], products = [] } = {}) {
  const orgById = Object.fromEntries(organizations.map((o) => [o.id, o]));
  const orgByName = Object.fromEntries(organizations.map((o) => [o.name, o]));
  const marketById = Object.fromEntries(markets.map((m) => [m.id, m]));
  const marketByName = Object.fromEntries(markets.map((m) => [m.name, m]));
  const marketByCode = Object.fromEntries(markets.map((m) => [m.code, m]));
  const brandById = Object.fromEntries(brands.map((b) => [b.id, b]));
  const brandByName = Object.fromEntries(brands.map((b) => [b.name, b]));
  const landingPageById = Object.fromEntries(landingPages.map((p) => [p.id, p]));
  const landingPageByTitle = Object.fromEntries(landingPages.map((p) => [p.title, p]));
  const facilityById = Object.fromEntries(facilities.map((f) => [f.id, f]));
  const productById = Object.fromEntries(products.map((p) => [p.id, p]));
  const productByName = Object.fromEntries(products.map((p) => [p.name, p]));

  return {
    orgById,
    orgByName,
    marketById,
    marketByName,
    marketByCode,
    brandById,
    brandByName,
    landingPageById,
    landingPageByTitle,
    facilityById,
    productById,
    productByName
  };
}

export function mapLandingPages(apiLandingPages = []) {
  return (apiLandingPages || []).map((p) => ({
    id: p.id,
    organizationId: p.organizationId,
    title: p.title,
    audience: p.audience,
    status: p.status,
    extras: p.extras || null
  }));
}

export function mapBrands(apiBrands = [], lookups) {
  const { marketById, orgById, landingPageById } = lookups;
  return (apiBrands || [])
    .filter((b) => b.active !== false)
    .map((b) => {
      const id = b?.id ?? b?.Id;
      const landingPageId = b?.landingPageId ?? b?.LandingPageId ?? null;
      const marketIds = b?.marketIds ?? b?.MarketIds ?? [];
      const organizationIds = b?.organizationIds ?? b?.OrganizationIds ?? [];

      return {
        id,
        name: b?.name ?? b?.Name,
        landingPageId,
        landingPage: landingPageId ? (landingPageById[landingPageId]?.title || '') : '',
        markets: (marketIds || []).map((mid) => marketById[mid]?.code || marketById[mid]?.name).filter(Boolean),
        organizations: (organizationIds || []).map((oid) => orgById[oid]?.name).filter(Boolean),
        products: 0
      };
    })
    // Ensure deterministic ordering for paginated UI + E2E (newest first).
    .sort((a, b) => {
      const aNum = Number(a?.id);
      const bNum = Number(b?.id);
      if (Number.isFinite(aNum) && Number.isFinite(bNum)) return bNum - aNum;
      return String(b?.id ?? '').localeCompare(String(a?.id ?? ''));
    });
}

export function mapProducts(apiProducts = [], lookups) {
  const { brandById, marketById } = lookups;

  return (apiProducts || []).map((p) => ({
    id: p.id,
    metrcProductId: p.metrcProductId ?? p.metrcProductID ?? p.metrcId ?? null,
    name: p.name,
    brandId: p.brandId || null,
    brand: p.brandId ? (brandById[p.brandId]?.name || '') : '',
    markets: (p.marketIds || []).map((id) => marketById[id]?.code || marketById[id]?.name).filter(Boolean),
    market: '',
    category: ['Flower', 'Edible', 'Vape', 'Topical'][p.productCategoryType] || 'Flower',
    categoryName: ['Flower', 'Edible', 'Vape', 'Topical'][p.productCategoryType] || 'Unknown',
    strain: p.strain || '',
    description: p.description || '',
    imageName: '',
    inventoryUpc: p.inventoryUpc || '',
    notesInternal: p.notes || '',
    isArchived: p.active === false,
    archivedAt: p.active === false ? (p.lastModified ? new Date(p.lastModified).toISOString() : new Date().toISOString()) : null
  }));
}

export function mapTemplates(apiTemplates = [], lookups) {
  const { marketById } = lookups;
  return (apiTemplates || []).filter((t) => t.active !== false).map((t) => ({
    id: t.id,
    name: t.name,
    markets: (t.marketIds || []).map((id) => marketById[id]?.code || marketById[id]?.name).filter(Boolean)
  }));
}

export function mapFacilities(apiFacilities = [], lookups) {
  const { orgById } = lookups;
  return (apiFacilities || []).filter((f) => f.active !== false).map((f) => ({
    id: f.id,
    organizationId: f.organizationId,
    org: orgById[f.organizationId]?.name || '',
    marketId: f.marketId,
    name: f.name,
    license: f.license,
    metrcFacilityId: f.metrcFacilityId
  }));
}

export function mapPackages(apiPackages = [], lookups) {
  const { marketById, facilityById, productById } = lookups;
  return (apiPackages || []).filter((p) => p.active !== false).map((p) => ({
    id: p.id,
    metrcPackageId: p.metrcPackageId,
    facility: p.facilityId ? (facilityById[p.facilityId]?.name || '') : '',
    facilityId: p.facilityId,
    market: p.marketId ? [marketById[p.marketId]?.code || marketById[p.marketId]?.name].filter(Boolean) : [],
    marketId: p.marketId,
    metrcFacilityId: p.metrcFacilityId,
    product: p.productId ? (productById[p.productId]?.name || '') : '',
    strain: p.productId ? (productById[p.productId]?.strain || '') : '',
    productId: p.productId,
    tagId: p.tagId,
    label: p.label,
    packageType: p.packageType,
    productionBatchNumber: p.productionBatchNumber,
    productName: p.productName,
    productCategoryName: p.productId ? (productById[p.productId]?.productCategoryName || '') : '',
    sourcePackageLabel: p.sourcePackageLabels,
    labTestingStateName: p.labTestingStateName,
    labTestingStateDate: p.labTestingStateDate,
    packagedDate: p.packagedDate,
    quantity: p.quantity,
    unitOfMeasureId: p.unitOfMeasureId,
    unitOfMeasureName: p.unitOfMeasureName,
    unitOfMeasure: UNITS_OF_MEASURE.find(
      (u) => u.Name.toLowerCase() === (p.unitOfMeasureName || '').toLowerCase()
    )?.Abbreviation ?? null,
    retailIdQrCount: p.retailIdQrCount,
    labelsLastGeneratedDateTime: p.labelsLastGeneratedDateTime,
    active: p.active,
    isOnHold: p.isOnHold ?? false,
    isOnRecall: p.isOnRecall ?? false,
  }));
}

export function mapPrintRuns(apiPrintRuns = []) {
  return (apiPrintRuns || []).filter((r) => r.active !== false).map((r) => ({
    ...r,
    id: r.id ?? r.Id,
    organizationId: r.organizationId ?? r.organization_id ?? r.OrganizationId,
    ownerOrganizationId: r.ownerOrganizationId ?? r.owner_organization_id ?? r.OwnerOrganizationId,
    allowReprints: r.allowReprints ?? r.allow_reprints ?? r.AllowReprints,
    jobId: r.jobId ?? r.job_id ?? r.JobId,
    jobName: r.jobName ?? r.job_name ?? r.JobName,
    name: r.name || r.jobName || r.job_name || r.JobName || '',
    templateId: r.templateId ?? r.template_id ?? r.TemplateId,
    templateName: r.templateName ?? r.template_name ?? r.TemplateName,
    template: r.template ?? r.templateName ?? r.template_name ?? '',
    packageId: r.packageId ?? r.package_id ?? r.PackageId,
    packageLabel: r.packageLabel ?? r.package_label ?? r.PackageLabel,
    packageCoaId: r.packageCoaId ?? r.package_coa_id ?? r.PackageCoaId,
    stagePackageCoaId: r.stagePackageCoaId ?? r.stage_package_coa_id ?? r.StagePackageCoaId,
    productId: r.productId ?? r.product_id ?? r.ProductId,
    productName: r.productName ?? r.product_name ?? r.ProductName,
    productOverride: r.productOverride ?? r.product_override ?? r.ProductOverride,
    productOverrideId: r.productOverrideId ?? r.product_override_id ?? r.ProductOverride,
    productOverrideName: r.productOverrideName ?? r.product_override_name ?? r.ProductOverrideName,
    product: r.product || r.productOverrideName || r.product_override_name || r.ProductOverrideName || r.productName || r.product_name || r.ProductName || '',
    count: r.count ?? r.Count,
    status: r.status ?? r.Status,
    packagedDate: r.packagedDate ?? r.packaged_date ?? r.PackagedDate,
    printedDate: r.printedDate ?? r.printed_date ?? r.PrintedDate,
    printDate: (r.printDate || r.print_date) ? new Date(r.printDate || r.print_date).toLocaleString() : '',
    labelTemplateData: r.labelTemplateData ?? r.label_template_data ?? r.LabelTemplateData,
    templateData: r.templateData ?? r.template_data ?? r.TemplateData ?? r.labelTemplateData ?? r.label_template_data ?? r.LabelTemplateData,
    qrs: r.qrs ?? r.Qrs ?? [],
    range: r.range ?? r.Range ?? [],
    offset: r.offset ?? r.Offset ?? 0,
    lastModified: r.lastModified ?? r.last_modified ?? r.LastModified,
  }));
}

export function toAddBrandPayloadFromUi(uiBrand, lookups) {
  const { marketByCode, marketByName, orgByName, landingPageByTitle } = lookups;
  return {
    name: uiBrand.name,
    landingPageId: uiBrand.landingPage ? (landingPageByTitle[uiBrand.landingPage]?.id || null) : null,
    marketIds: (uiBrand.markets || []).map((m) => marketByCode[m]?.id ?? marketByName[m]?.id).filter(Boolean),
    organizationIds: (uiBrand.organizations || []).map((o) => orgByName[o]?.id).filter(Boolean)
  };
}

export function toEditBrandPayloadFromUi(uiBrand, lookups) {
  const { marketByCode, marketByName, orgByName, landingPageByTitle } = lookups;
  return {
    brandId: uiBrand.id,
    name: uiBrand.name,
    landingPageId: uiBrand.landingPage ? (landingPageByTitle[uiBrand.landingPage]?.id || null) : null,
    marketIds: (uiBrand.markets || []).map((m) => marketByCode[m]?.id ?? marketByName[m]?.id).filter(Boolean),
    organizationIds: (uiBrand.organizations || []).map((o) => orgByName[o]?.id).filter(Boolean)
  };
}

export function toAddProductPayloadFromUi(uiProduct, { organizationId }, lookups) {
  const { brandByName, marketByCode, marketByName } = lookups;
  const categoryInt = CATEGORY_TO_INT[uiProduct.category] ?? 0;
  return {
    organizationId,
    brandId: brandByName[uiProduct.brand]?.id || null,
    productImageId: null,
    name: uiProduct.name,
    productCategoryTypeId: categoryInt,
    inventoryUpc: uiProduct.inventoryUpc || '',
    strain: uiProduct.strain || '',
    description: uiProduct.description || '',
    notes: uiProduct.notesInternal || '',
    shelfLifeDays: uiProduct.shelfLifeDays ?? null,
    marketIds: (uiProduct.markets || []).map((m) => marketByCode[m]?.id ?? marketByName[m]?.id).filter(Boolean)
  };
}

export function toEditProductPayloadFromUi(existingApiProduct, uiProduct, lookups) {
  const { marketByCode, marketByName, brandByName } = lookups;
  const categoryInt = CATEGORY_TO_INT[uiProduct.category] ?? existingApiProduct.productCategoryType ?? 0;
  return {
    productId: existingApiProduct.id,
    brandId: brandByName[uiProduct.brand]?.id || existingApiProduct.brandId,
    name: uiProduct.name,
    productCategoryType: categoryInt,
    inventoryUpc: uiProduct.inventoryUpc || '',
    strain: uiProduct.strain || '',
    description: uiProduct.description || '',
    notes: uiProduct.notesInternal || '',
    shelfLifeDays: uiProduct.shelfLifeDays ?? null,
    marketIds: (uiProduct.markets || []).map((m) => marketByCode[m]?.id ?? marketByName[m]?.id).filter(Boolean)
  };
}
